import { dynamicsSourceHref } from '../../utils/dynamicsLinks';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useDynamicsContext } from '../../hooks/useDynamicsPage';
import useDynamicsWakeWord from '../../hooks/useDynamicsWakeWord';
import DocumentationAssistant from '../dashboard/DocumentationAssistant';
import DynamicsIcon from './DynamicsIcon';

export default function DynamicsLauncher() {
  const {user, isInternalUser} = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const page = useDynamicsContext();
  const [open, setOpen] = useState(false);
  const [welcome, setWelcome] = useState(false);
  const [robotState, setRobotState] = useState('idle');
  const [wakeToken, setWakeToken] = useState(0);
  const [wakeCommand, setWakeCommand] = useState('');
  const visible = !!user && location.pathname !== '/login' && location.pathname !== '/dashboard/tickets';
  const welcomeKey = user ? `dynamics-welcome-seen:${user.uid}` : null;
  const dismissWelcome = () => {
    setWelcome(false);
    if (welcomeKey) { try { localStorage.setItem(welcomeKey, 'true'); } catch {} }
  };
  const showAssistant = () => { dismissWelcome(); setOpen(true); };
  const wakeWord = useDynamicsWakeWord({
    active:visible && !open,
    userId:user?.uid,
    onWake:request => {dismissWelcome();setWakeCommand(request?.command || '');setOpen(true);setWakeToken(value => value + 1);},
  });
  useEffect(() => {
    const show = () => { setWelcome(false); setOpen(true); };
    window.addEventListener('dynamics:open', show);
    return () => window.removeEventListener('dynamics:open', show);
  }, []);
  useEffect(() => {
    if (!visible || !welcomeKey) { setWelcome(false); return; }
    try {
      const firstVisit = localStorage.getItem(welcomeKey) !== 'true';
      setWelcome(firstVisit);
      if (firstVisit) localStorage.setItem(welcomeKey, 'true');
    } catch { setWelcome(false); }
  }, [visible, welcomeKey]);
  useEffect(() => {
    document.body.classList.toggle('dynamics-panel-open', open && visible);
    return () => document.body.classList.remove('dynamics-panel-open');
  }, [open, visible]);
  if (!visible) return null;
  const context = page?.path === location.pathname ? page : { path: location.pathname, title: 'Projet actif', kind: 'project' };
  const projectId = context.projectId || Number(localStorage.getItem('selected_project_id'));
  const contextKey = JSON.stringify([user.uid, projectId]);
  return <>
    {welcome && !open && <section className="dynamics-welcome" aria-label="Bienvenue de Dynamix">
      <button type="button" className="dynamics-welcome-close" onClick={dismissWelcome} aria-label="Fermer la bulle de bienvenue">×</button>
      <p><strong>Bonjour, je suis Dynamix.</strong><br />Je suis là pour vous aider à suivre vos tickets et vos tests. Cliquez sur mon visage quand vous avez besoin de moi.</p>
    </section>}
    {wakeWord.enabled && !open && <span className={`dynamics-wake-indicator ${wakeWord.listening ? 'is-listening' : ''}`} role="status"><span />{wakeWord.listening ? 'Dites « Dynamix »' : 'Réveil vocal en attente'}</span>}
    <button type="button" onClick={open ? () => setOpen(false) : showAssistant} aria-label={open ? 'Réduire Dynamix' : 'Ouvrir Dynamix'} aria-expanded={open} className="dynamics-face-launcher"><DynamicsIcon size={64} state={robotState}/></button>
    <DocumentationAssistant key={contextKey} open={open} onClose={() => setOpen(false)} floating pageContext={context}
      onActivityState={setRobotState}
      wakeWord={wakeWord}
      autoStartLiveToken={wakeToken}
      autoStartLivePrompt={wakeCommand}
      project={{odooProjectId:projectId,name:context.projectName || localStorage.getItem('selected_project_name') || 'Projet actif'}}
      documentationId={context.documentationId} version={context.version || 'latest'} isInternal={isInternalUser}
      onApplyFilters={filters => {
        const params = new URLSearchParams();
        for (const key of ['search', 'stage', 'dateFrom', 'dateTo', 'dateField']) {
          if (typeof filters[key] === 'string') params.set(key, filters[key]);
        }
        params.set('dynamics', '1');
        navigate(`/dashboard/ticket-situation?${params}`);
      }}
      onUiAction={action => new Promise((resolve, reject) => {
        window.dispatchEvent(new CustomEvent('dynamics:ui-action', {detail:{action,resolve,reject}}));
      })}
      onNavigateSource={source => {
        if (source.type !== 'documentation') {
          const href = dynamicsSourceHref(source);
          navigate(href, href?.split('?')[0] === location.pathname ? {state:location.state} : undefined);
          return;
        }
        if (location.pathname !== '/dashboard/documentation') navigate('/dashboard/documentation', {state:{dynamicsSource:source}});
        else window.dispatchEvent(new CustomEvent('dynamics:source', {detail:source}));
      }}/>
  </>;
}
